import React, { FC, useEffect, useRef, useState } from 'react';
import { Dimensions, View, Text, TouchableOpacity, ViewStyle, StyleProp, ScrollView, StyleSheet } from 'react-native';
import i18n from 'i18n-js';

import { scale as initialScale, bottomBarHeight } from '@components/frame';
import ColorsWrapper from '@styles/colors';
import { font as initialFont } from '@styles/fonts';
import { isWeb } from '@helpers';
import { getBoxShadowStyle } from '@styles/shadow';
import ModalWrapper from '@components/modal/ModalWrapper';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import { BOTTOM_SHEET_TOP_OFFSET } from '@v2/pages/common/constants/base.constants';
import AttachItem from './AttachItem';
import { ColorFunction } from '@styles/colors/types';
import { DocType, ImageDocumentStoreItem, RetrievedDocumentReferenceLink } from '@v2/components/imageHandling/types/types';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { useManageAttachModal } from './hooks/useManageAttachModal';
import Svg from '@styles/icons/svg';
import { ATTACH_ITEM_STYLES, ATTACH_ITEMS_LIST_STYLES, ICON_TEXT_GAP } from '@v2/components/imageHandling/components/Header/constants';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

export interface AttachmentData {
  documentId: number;
  documentLinks: RetrievedDocumentReferenceLink[];
  documentTags: DocType[];
}

export interface AttachModalProps {
  C: ColorFunction;
  visible: boolean;
  onAttach?: (attachmentData: AttachmentData) => void;
  onToggleFavorite: (params: { scopeId: number; scopeReferenceId: number; img: ImageDocumentStoreItem }) => void;
  onRemoveAttachment: (link: RetrievedDocumentReferenceLink) => void;
  links: RetrievedDocumentReferenceLink[];
  imageId?: number;
  fileName?: string;
  img: ImageDocumentStoreItem | null;
  styles?: StyleProp<ViewStyle>;
  onClose: () => void;
  onNavigate?: () => void;
  scale?: ScaleFunction;
  font?: typeof initialFont;
}

const AttachModal: FC<AttachModalProps> = ({
  C,
  visible,
  onAttach,
  onToggleFavorite,
  onRemoveAttachment,
  links = [],
  imageId,
  fileName,
  img,
  styles,
  onClose,
  onNavigate,
  scale = initialScale,
  font = initialFont,
}) => {
  const shadowStyles = getBoxShadowStyle({ type: 'Ultra Wide', C, scheme: 'light' });

  const { safeIsOperatorPanelInfoScreen } = useNavigationCheck();

  const { onAttachPress } = useManageAttachModal({ fileName, imageId, onAttach, onClose, img });

  const containerRef = useRef<View>(null);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!visible) return;

    containerRef.current?.measureInWindow((_x: number, y: number) => {
      const screenHeight = Dimensions.get('window').height;
      const bottomOffset = ATTACH_ITEMS_LIST_STYLES.marginBottom + (isWeb ? 0 : bottomBarHeight);
      const availableHeight = screenHeight - y - bottomOffset;
      setMaxHeight(availableHeight);
    });
  }, [visible]);

  if (!visible) return null;

  return (
    <ModalWrapper isVisible={true} onBackdropPress={onClose} C={C} backdropColor="transparent" backdropOpacity={1}>
      <View
        testID={TEST_IDS.documentsAttachModal.root}
        ref={containerRef}
        style={[
          {
            position: 'absolute',
            top: scale(!safeIsOperatorPanelInfoScreen || isWeb ? 70 : 70 + BOTTOM_SHEET_TOP_OFFSET / 2),
            right: scale(18),
            width: scale(540),
            backgroundColor: C('Background/Light/Modal Highlight'),
            borderRadius: scale(36),
            padding: scale(18),
            overflow: 'hidden',
            zIndex: 100000000,
          },
          shadowStyles,
          styles,
          maxHeight != null && { maxHeight },
        ]}
      >
        {/* Header when there are items */}
        {links.length > 0 && (
          <Text
            style={[
              font.Headline,
              {
                color: C('Label/Light/Primary'),
                marginBottom: scale(12),
                marginLeft: scale(9),
              },
            ]}
          >
            {i18n.t('Attached To')}
          </Text>
        )}

        <TouchableOpacity testID={TEST_IDS.documentsAttachModal.attachToOther} onPress={onAttachPress} activeOpacity={0.8}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: scale(ICON_TEXT_GAP),
              paddingHorizontal: scale(ATTACH_ITEM_STYLES.paddingHorizontal),
              borderStyle: 'dashed',
              borderColor: C('Hairline/Light/No trans [mobile]'),
              borderWidth: StyleSheet.hairlineWidth,
              borderRadius: scale(ATTACH_ITEM_STYLES.borderRadius),
              height: scale(ATTACH_ITEM_STYLES.height),
              marginBottom: scale(10),
            }}
          >
            <Svg icon="attach" width={scale(24)} height={scale(24)} color="Link/Light/Primary" />
            <Text
              style={[
                font['Button 1'],
                {
                  color: C('Link/Light/Primary'),
                  fontSize: scale(16),
                  // display: 'flex',
                  // alignItems: 'center',

                  lineHeight: scale(46),
                },
              ]}
            >
              {links.length > 0 ? i18n.t('Attach To Other Item...') : i18n.t('Attach To...')}
            </Text>
          </View>
        </TouchableOpacity>

        {/* Show different content based on whether there are items */}
        <ScrollView showsVerticalScrollIndicator={false} style={{ flex: 1 }}>
          {links?.map((item, idx) => (
            <View key={idx} style={{ marginTop: scale(8) }}>
              <AttachItem
                scale={scale}
                font={font}
                link={item}
                onPress={() => {
                  // onClose();
                  onNavigate?.();
                }}
                onToggleFavorite={() => {
                  onToggleFavorite?.({ scopeId: item.scopeId, scopeReferenceId: item.scopeReferenceId, img });
                }}
                onRemove={() => {
                  onRemoveAttachment?.(item);
                }}
              />
            </View>
          ))}
        </ScrollView>
      </View>
    </ModalWrapper>
  );
};

export default ColorsWrapper(AttachModal);
